import { useState, useEffect, useMemo, Fragment } from 'react';
import { FileText, Phone, MapPin, ChevronDown, ChevronUp, Calendar, Check, Download, Truck, FileSpreadsheet, Trophy, RotateCcw, AlertTriangle, Edit2, CheckSquare, Square, History, Archive, Search, X } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import api from '../lib/api';

// Format price with comma as decimal separator
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0,00';
    return value.toFixed(2).replace('.', ',');
};

// Format date
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ro-RO', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
    });
};

// Get month-year key from date
const getMonthYearKey = (dateStr) => {
    if (!dateStr) return 'necunoscut';
    const date = new Date(dateStr);
    const months = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
};

export const FacturiV2 = () => {
    const [loading, setLoading] = useState(true);
    const [orders, setOrders] = useState([]);
    const [expandedOrders, setExpandedOrders] = useState({});
    const [summary, setSummary] = useState({ total_orders: 0, total_value: 0 });
    const [settings, setSettings] = useState({ invoice_prefix: 'TOTH' });
    const [courierData, setCourierData] = useState({});
    const [topCustomers, setTopCustomers] = useState([]);
    const [showTopCustomers, setShowTopCustomers] = useState(false);
    const [stornoConfirm, setStornoConfirm] = useState(null); // {customer_id, invoice_number}
    const [cancelStornoConfirm, setCancelStornoConfirm] = useState(null); // {order, invoice_number}
    const [stornoHistory, setStornoHistory] = useState([]);
    const [showStornoHistory, setShowStornoHistory] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', address: '', phone: '', date: '', items: [] });
    const [showHistory, setShowHistory] = useState(false);
    const [expandedMonths, setExpandedMonths] = useState({});
    
    // Invoice number edit state
    const [editingInvoiceNumber, setEditingInvoiceNumber] = useState(null); // order object
    const [newInvoiceNumber, setNewInvoiceNumber] = useState('');
    
    // Customer search for edit dialog
    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    
    // Selection state for batch export
    const [selectedInvoices, setSelectedInvoices] = useState({});
    const [selectMode, setSelectMode] = useState(false);
    
    // Search state
    const [searchQuery, setSearchQuery] = useState('');
    
    // Generate Storno modal state
    const [generateStornoModal, setGenerateStornoModal] = useState(null); // {order, invoiceNumber}
    const [stornoIncludeTransport, setStornoIncludeTransport] = useState(true);
    const [generatingStorno, setGeneratingStorno] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [ordersRes, settingsRes, topRes, customersRes] = await Promise.all([
                api.get('/orders/v2/completed'),
                api.get('/settings/v2'),
                api.get('/orders/v2/top-customers'),
                api.get('/orders/v2/customers')
            ]);
            
            const data = ordersRes.data || [];
            setOrders(data);
            setSettings(settingsRes.data || { invoice_prefix: 'TOTH' });
            setTopCustomers(topRes.data || []);
            setCustomers(customersRes.data || []);
            
            // Initialize courier data from orders
            const couriers = {};
            data.forEach(o => {
                couriers[o.customer_id] = o.courier || false;
            });
            setCourierData(couriers);
            
            // Calculate summary
            const totalValue = data.reduce((sum, o) => sum + (o.total || 0), 0);
            setSummary({
                total_orders: data.length,
                total_value: totalValue
            });
        } catch (err) {
            toast.error('Eroare la încărcarea facturilor');
            console.error(err);
        }
        setLoading(false);
    };

    // Separate orders into active and archived
    const activeOrders = useMemo(() => orders.filter(o => !o.archived), [orders]);
    const archivedOrders = useMemo(() => orders.filter(o => o.archived), [orders]);
    
    // Group archived orders by month
    const archivedByMonth = useMemo(() => {
        const grouped = {};
        archivedOrders.forEach(order => {
            const monthKey = getMonthYearKey(order.exported_at || order.completed_at);
            if (!grouped[monthKey]) {
                grouped[monthKey] = [];
            }
            grouped[monthKey].push(order);
        });
        // Sort months descending
        return Object.entries(grouped).sort((a, b) => {
            const dateA = new Date(archivedOrders.find(o => getMonthYearKey(o.exported_at || o.completed_at) === a[0])?.exported_at || 0);
            const dateB = new Date(archivedOrders.find(o => getMonthYearKey(o.exported_at || o.completed_at) === b[0])?.exported_at || 0);
            return dateB - dateA;
        });
    }, [archivedOrders]);

    const toggleMonth = (monthKey) => {
        setExpandedMonths(prev => ({ ...prev, [monthKey]: !prev[monthKey] }));
    };

    // Sort orders descending by invoice number (or completed_at if no invoice)
    const sortedOrders = useMemo(() => {
        return [...activeOrders].sort((a, b) => {
            // First try to sort by invoice_number
            const numA = parseInt((a.invoice_number || '').replace(/\D/g, '')) || 0;
            const numB = parseInt((b.invoice_number || '').replace(/\D/g, '')) || 0;
            if (numA !== numB) {
                return numB - numA; // Descending
            }
            // Fallback: sort by completed_at (newest first)
            const dateA = new Date(a.completed_at || 0).getTime();
            const dateB = new Date(b.completed_at || 0).getTime();
            return dateB - dateA;
        });
    }, [activeOrders]);

    // Filter orders by search query (name or customer_id) - INCLUDE ARCHIVED!
    const filteredOrders = useMemo(() => {
        if (!searchQuery.trim()) return sortedOrders;
        const query = searchQuery.toLowerCase().trim();
        return sortedOrders.filter(order => {
            const customerName = (order.customer_name || order.name || '').toLowerCase();
            const customerId = (order.customer_id || '').toLowerCase();
            const invoiceNumber = (order.invoice_number || '').toLowerCase();
            return customerName.includes(query) || 
                   customerId.includes(query) || 
                   invoiceNumber.includes(query);
        });
    }, [sortedOrders, searchQuery]);

    // Filter archived orders by search query
    const filteredArchivedOrders = useMemo(() => {
        if (!searchQuery.trim()) return archivedOrders;
        const query = searchQuery.toLowerCase().trim();
        return archivedOrders.filter(order => {
            const customerName = (order.customer_name || order.name || '').toLowerCase();
            const customerId = (order.customer_id || '').toLowerCase();
            const invoiceNumber = (order.invoice_number || '').toLowerCase();
            return customerName.includes(query) || 
                   customerId.includes(query) || 
                   invoiceNumber.includes(query);
        });
    }, [archivedOrders, searchQuery]);

    // Group filtered archived orders by month
    const filteredArchivedByMonth = useMemo(() => {
        const grouped = {};
        filteredArchivedOrders.forEach(order => {
            const monthKey = getMonthYearKey(order.exported_at || order.completed_at);
            if (!grouped[monthKey]) {
                grouped[monthKey] = [];
            }
            grouped[monthKey].push(order);
        });
        return Object.entries(grouped).sort((a, b) => {
            const dateA = new Date(filteredArchivedOrders.find(o => getMonthYearKey(o.exported_at || o.completed_at) === a[0])?.exported_at || 0);
            const dateB = new Date(filteredArchivedOrders.find(o => getMonthYearKey(o.exported_at || o.completed_at) === b[0])?.exported_at || 0);
            return dateB - dateA;
        });
    }, [filteredArchivedOrders]);

    const toggleOrder = (order) => {
        // Use order_id if available, otherwise invoice_number, otherwise customer_id
        const key = order.order_id || order.invoice_number || order.customer_id;
        setExpandedOrders(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };
    
    const isOrderExpanded = (order) => {
        const key = order.order_id || order.invoice_number || order.customer_id;
        return expandedOrders[key];
    };

    // Filter customers for search
    const filteredCustomers = useMemo(() => {
        if (!customerSearch.trim()) return customers.slice(0, 10);
        const search = customerSearch.toLowerCase();
        return customers.filter(c => 
            c.customer_id?.toLowerCase().includes(search) ||
            c.name?.toLowerCase().includes(search) ||
            c.phone?.includes(search)
        ).slice(0, 10);
    }, [customers, customerSearch]);

    // Select customer and fill form
    const selectCustomer = (customer) => {
        setEditForm(prev => ({
            ...prev,
            name: customer.name || prev.name,
            phone: customer.phone || prev.phone,
            address: customer.address || prev.address
        }));
        setCustomerSearch('');
        setShowCustomerDropdown(false);
    };

    const isStornoGroup = (o) => o.stornata || !!o.has_storno;

    const getInvoiceNumber = (order, index) => {
        // Use invoice_number from database if available
        if (order.invoice_number) {
            return order.invoice_number;
        }
        // Fallback to generated number (for old orders without invoice_number)
        const prefix = settings.invoice_prefix || 'TOTH';
        const startNum = settings.invoice_start_number || 1;
        const num = startNum + index;
        return `${prefix}${String(num).padStart(4, '0')}`;
    };

    const handleCourierChange = async (customerId, value) => {
        setCourierData(prev => ({ ...prev, [customerId]: value }));
        
        // Save to backend
        try {
            await api.put(`/orders/v2/customers/${customerId}/courier`, { courier: value });
        } catch (err) {
            console.error('Error saving courier:', err);
        }
    };

    // Storno invoice
    const handleStorno = async (order) => {
        try {
            // Use order_id if available, otherwise invoice_number, otherwise customer_id
            const identifier = order.order_id || order.invoice_number || order.customer_id;
            await api.post(`/orders/v2/storno/${encodeURIComponent(identifier)}`);
            toast.success('Factura a fost stornată. Produsele au fost returnate în magazin.');
            setStornoConfirm(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la stornare');
        }
    };

    // Cancel storno (revalidate invoice)
    const handleCancelStorno = async (order) => {
        try {
            const identifier = order.order_id || order.invoice_number || order.customer_id;
            await api.post(`/orders/v2/cancel-storno/${encodeURIComponent(identifier)}`);
            toast.success('Stornarea a fost anulată. Factura este din nou validă.');
            setCancelStornoConfirm(null);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la anularea stornării');
        }
    };

    // Load storno history
    const loadStornoHistory = async (invoiceNumber = null) => {
        try {
            const url = invoiceNumber 
                ? `/storno-history?invoice_number=${encodeURIComponent(invoiceNumber)}`
                : '/storno-history';
            const res = await api.get(url);
            setStornoHistory(res.data || []);
            setShowStornoHistory(true);
        } catch (err) {
            toast.error('Eroare la încărcarea istoricului');
        }
    };

    // Generate Storno Invoice - creates NEW invoice with negative values
    const handleGenerateStorno = async () => {
        if (!generateStornoModal) return;
        
        const { order, invoiceNumber } = generateStornoModal;
        setGeneratingStorno(true);
        
        try {
            const identifier = order.order_id || invoiceNumber || order.customer_id;
            const res = await api.post(`/orders/v2/generate-storno/${encodeURIComponent(identifier)}`, {
                include_transport: stornoIncludeTransport
            });
            
            toast.success(`Factură storno ${res.data.storno_invoice_number} generată!`, { duration: 5000 });
            setGenerateStornoModal(null);
            setStornoIncludeTransport(true);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la generarea facturii storno');
        }
        
        setGeneratingStorno(false);
    };

    // Open generate storno modal
    const openGenerateStornoModal = (order, invoiceNumber) => {
        setGenerateStornoModal({ order, invoiceNumber });
        setStornoIncludeTransport(true);
    };

    // Edit invoice
    const handleEditClick = (order) => {
        const items = (order.items || []).map(item => ({
            id: item.id,
            name: item.name,
            barcode: item.barcode,
            quantity: item.quantity,
            unit_price: item.unit_price,
            is_fee: item.is_fee
        }));
        
        // Get date in YYYY-MM-DD format for input type="date"
        // Priority: invoice_date > completed_at
        let dateValue = '';
        if (order.invoice_date) {
            // invoice_date is already in YYYY-MM-DD format
            dateValue = order.invoice_date;
        } else if (order.completed_at) {
            const d = new Date(order.completed_at);
            dateValue = d.toISOString().split('T')[0]; // Format: YYYY-MM-DD
        }
        
        setEditForm({
            name: order.customer_name || order.name || '',
            address: order.address || '',
            phone: order.phone || '',
            date: dateValue,
            items: items
        });
        setEditingInvoice(order);
    };

    const handleEditSave = async () => {
        if (!editingInvoice) return;
        
        try {
            await api.put(`/orders/v2/invoice/${encodeURIComponent(editingInvoice.invoice_number)}/edit`, {
                customer_name: editForm.name,
                customer_address: editForm.address,
                customer_phone: editForm.phone,
                invoice_date: editForm.date,
                items: editForm.items
            });
            toast.success('Datele facturii au fost actualizate');
            setEditingInvoice(null);
            // Reload orders silently - don't show error if this fails
            try {
                loadData();
            } catch (e) {
                console.log('Reload after edit:', e);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la salvare');
        }
    };

    const updateItemField = (itemId, field, value) => {
        setEditForm(prev => ({
            ...prev,
            items: prev.items.map(item => 
                item.id === itemId ? { ...item, [field]: value } : item
            )
        }));
    };

    const calculateEditTotal = () => {
        return editForm.items.reduce((sum, item) => {
            return sum + (parseFloat(item.unit_price) || 0) * (parseInt(item.quantity) || 0);
        }, 0);
    };

    const handleExportPDF = async (order, invoiceNumber) => {
        try {
            // Send completed_at to identify specific order
            // Also send version for TVA columns in V3 mode
            const completedAt = encodeURIComponent(order.completed_at || '');
            const appVersion = localStorage.getItem('app_version') || 'v2';
            const response = await api.get(`/orders/v2/invoice/${order.customer_id}/pdf?invoice_number=${encodeURIComponent(invoiceNumber)}&completed_at=${completedAt}&version=${appVersion}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invoiceNumber}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('PDF descărcat!');
        } catch (err) {
            toast.error('Eroare la export PDF');
            console.error(err);
        }
    };

    const handleExportDBF = async (order, invoiceNumber) => {
        try {
            // Send completed_at to identify specific order
            const completedAt = encodeURIComponent(order.completed_at || '');
            const response = await api.get(`/orders/v2/invoice/${order.customer_id}/excel?invoice_number=${encodeURIComponent(invoiceNumber)}&completed_at=${completedAt}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${invoiceNumber}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Excel descărcat! Factura mutată în Istoric.');
            // Reload data to move invoice to history
            loadData();
        } catch (err) {
            toast.error('Eroare la export Excel');
            console.error(err);
        }
    };

    // Archive invoice manually (for old invoices already downloaded)
    const archiveInvoice = async (order) => {
        try {
            // Use order_id if available, otherwise fall back to customer_id
            const identifier = order.order_id || order.customer_id;
            await api.post(`/orders/v2/archive/${encodeURIComponent(identifier)}`, {
                completed_at: order.completed_at,
                order_id: order.order_id,
                invoice_number: order.invoice_number
            });
            toast.success('Factură mutată în Istoric');
            loadData();
        } catch (err) {
            toast.error('Eroare la arhivare');
            console.error(err);
        }
    };

    // Unarchive invoice (return from history to active)
    const unarchiveInvoice = async (order) => {
        try {
            await api.post(`/orders/v2/unarchive/${order.customer_id}`, {
                completed_at: order.completed_at
            });
            toast.success('Factură returnată în lista activă');
            loadData();
        } catch (err) {
            toast.error('Eroare la returnare');
            console.error(err);
        }
    };

    // Edit invoice number
    const startEditInvoiceNumber = (order) => {
        const currentNumber = order.invoice_number || `${settings.invoice_prefix}${order.customer_id}`;
        setEditingInvoiceNumber(order);
        setNewInvoiceNumber(currentNumber);
    };

    const saveInvoiceNumber = async () => {
        if (!editingInvoiceNumber || !newInvoiceNumber.trim()) {
            toast.error('Introdu numărul de factură');
            return;
        }
        try {
            await api.put(`/orders/v2/invoice/${editingInvoiceNumber.customer_id}/number`, {
                invoice_number: newInvoiceNumber.trim(),
                completed_at: editingInvoiceNumber.completed_at
            });
            toast.success('Număr factură actualizat');
            setEditingInvoiceNumber(null);
            setNewInvoiceNumber('');
            loadData();
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Eroare la actualizare';
            // Check if error contains a suggestion for next available number
            const match = errorMsg.match(/Următorul disponibil: ([A-Za-z0-9]+)/);
            if (match) {
                const suggestedNumber = match[1];
                toast.error(`Numărul ${newInvoiceNumber} există deja!`, {
                    description: `Folosește ${suggestedNumber} în schimb`,
                    action: {
                        label: 'Folosește',
                        onClick: () => setNewInvoiceNumber(suggestedNumber)
                    },
                    duration: 10000
                });
            } else {
                toast.error(errorMsg);
            }
            console.error(err);
        }
    };

    // Toggle invoice selection
    const toggleInvoiceSelection = (invoiceNumber) => {
        setSelectedInvoices(prev => ({
            ...prev,
            [invoiceNumber]: !prev[invoiceNumber]
        }));
    };

    // Get selected invoice numbers
    const getSelectedInvoiceNumbers = () => {
        return Object.entries(selectedInvoices)
            .filter(([_, selected]) => selected)
            .map(([invNum, _]) => invNum);
    };

    // Get selected orders with their data
    const getSelectedOrdersData = () => {
        const selected = getSelectedInvoiceNumbers();
        return sortedOrders
            .map((order, idx) => ({
                ...order,
                generated_invoice_number: getInvoiceNumber(order, idx)
            }))
            .filter(order => selected.includes(order.generated_invoice_number || getInvoiceNumber(order, sortedOrders.indexOf(order))));
    };

    const selectedCount = getSelectedInvoiceNumbers().length;

    // Export multiple PDFs as ZIP
    const handleExportMultiplePDF = async () => {
        const selectedOrders = getSelectedOrdersData();
        if (selectedOrders.length === 0) {
            toast.error('Selectează cel puțin o factură');
            return;
        }
        
        try {
            // Send customer_ids, invoice numbers and completed_at to identify specific orders
            // Also include version for TVA columns in V3 mode
            const appVersion = localStorage.getItem('app_version') || 'v2';
            const exportData = selectedOrders.map((order) => ({
                customer_id: order.customer_id,
                invoice_number: order.generated_invoice_number,
                completed_at: order.completed_at || ''
            }));
            
            const response = await api.post('/orders/v2/export-multiple-pdf', 
                { orders: exportData, version: appVersion },
                { responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `facturi_pdf_${selectedOrders.length}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success(`ZIP cu ${selectedOrders.length} PDF-uri descărcat!`);
        } catch (err) {
            toast.error('Eroare la export PDF multiplu');
            console.error(err);
        }
    };

    // Export multiple DBFs as ZIP
    const handleExportMultipleDBF = async () => {
        const selectedOrders = getSelectedOrdersData();
        if (selectedOrders.length === 0) {
            toast.error('Selectează cel puțin o factură');
            return;
        }
        
        try {
            const exportData = selectedOrders.map((order) => ({
                customer_id: order.customer_id,
                invoice_number: order.generated_invoice_number,
                completed_at: order.completed_at || ''
            }));
            
            const response = await api.post('/orders/v2/export-multiple-dbf', 
                { orders: exportData },
                { responseType: 'blob' }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `facturi_dbf_${selectedOrders.length}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success(`ZIP cu ${selectedOrders.length} DBF-uri descărcat!`);
        } catch (err) {
            toast.error('Eroare la export DBF multiplu');
            console.error(err);
        }
    };

    // Clear selection
    const clearSelection = () => {
        setSelectedInvoices({});
        setSelectMode(false);
    };

    // Select all visible invoices
    const selectAllInvoices = () => {
        const allSelected = {};
        sortedOrders.forEach(order => {
            if (order.invoice_number && !order.stornata) {
                allSelected[order.invoice_number] = true;
            }
        });
        setSelectedInvoices(allSelected);
    };

    const renderArchivedOrder = (order) => {
        const invoiceNumber = order.invoice_number || `${settings.invoice_prefix}${order.customer_id}`;
        const isStornata = order.stornata;
        return (
                                                    <div
 key={order.order_id || order.customer_id + '-' + order.invoice_number}
                                                        className={`bg-white border rounded-xl overflow-hidden ${
                                                            isStornata ? 'border-red-300 bg-red-50' : 'border-zinc-200'
                                                        }`}
                                                    >
                                                        {/* Order Header */}
                                                        <div className={`w-full p-4 flex items-center justify-between transition-colors ${
                                                            isStornata ? 'hover:bg-red-100' : 'hover:bg-zinc-50'
                                                        }`}>
                                                            <button
                                                                onClick={() => toggleOrder(order)}
                                                                className="flex items-center gap-3"
                                                            >
                                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                                    isStornata ? 'bg-red-200' : 'bg-zinc-100'
                                                                }`}>
                                                                    <span className={`font-bold text-sm ${
                                                                        isStornata ? 'text-red-700' : 'text-zinc-700'
                                                                    }`}>{order.customer_id}</span>
                                                                </div>
                                                                <div className="text-left">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={`font-bold ${
                                                                            isStornata ? 'text-red-600 line-through' : 'text-violet-600'
                                                                        }`}>{invoiceNumber}</span>
                                                                        {isStornata && (
                                                                            <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">
                                                                                STORNATĂ
                                                                            </span>
                                                                        )}
                                                                        {order.name && <span className="text-zinc-500">- {order.name}</span>}
                                                                    </div>
                                                                    <div className="flex items-center gap-3 text-xs text-zinc-400">
                                                                        <span>{order.item_count} produse</span>
                                                                        {order.completed_at && (
                                                                            <span className="flex items-center gap-1">
                                                                                <Calendar size={10} />
                                                                                {formatDate(order.completed_at)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </button>
                                                            <div className="flex items-center gap-2">
                                                                <Button
                                                                    onClick={(e) => { e.stopPropagation(); handleExportPDF(order, invoiceNumber); }}
                                                                    size="sm"
                                                                    className="bg-red-500 hover:bg-red-600 h-8 px-2"
                                                                    disabled={isStornata || order.has_storno}
                                                                >
                                                                    <Download size={14} className="mr-1" />
                                                                    PDF
                                                                </Button>
                                                                <Button
                                                                    onClick={(e) => { e.stopPropagation(); handleExportDBF(order, invoiceNumber); }}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-blue-300 text-blue-600 hover:bg-blue-50 h-8 px-2"
                                                                    disabled={isStornata || order.has_storno}
                                                                >
                                                                    <FileSpreadsheet size={14} className="mr-1" />
                                                                    Excel
                                                                </Button>
                                                                <Button
                                                                    onClick={(e) => { e.stopPropagation(); openGenerateStornoModal(order, invoiceNumber); }}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-orange-300 text-orange-600 hover:bg-orange-50 h-8 px-2"
                                                                    title="Generează factură storno"
                                                                    disabled={isStornata || order.has_storno}
                                                                >
                                                                    <RotateCcw size={14} className="mr-1" />
                                                                    Storno
                                                                </Button>
                                                                <Button
                                                                    onClick={(e) => { e.stopPropagation(); unarchiveInvoice(order); }}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="border-amber-300 text-amber-600 hover:bg-amber-50 h-8 px-2"
                                                                    title="Returnează în lista activă"
                                                                >
                                                                    <RotateCcw size={14} />
                                                                </Button>
                                                                {order.has_storno && (
                                                                    <span className="text-xs text-orange-600 font-medium px-2 py-0.5 bg-orange-100 rounded">
                                                                        Storno: {order.has_storno}
                                                                    </span>
                                                                )}
                                                                <span className={`font-bold text-lg ${
                                                                    isStornata ? 'text-red-600 line-through' : 'text-emerald-600'
                                                                }`}>
                                                                    {formatPrice(order.total)} RON
                                                                </span>
                                                                <button onClick={() => toggleOrder(order)}>
                                                                    {isOrderExpanded(order) ? (
                                                                        <ChevronUp size={18} className="text-zinc-400" />
                                                                    ) : (
                                                                        <ChevronDown size={18} className="text-zinc-400" />
                                                                    )}
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Expanded Details */}
                                                        {isOrderExpanded(order) && (
                                                            <div className="border-t border-zinc-100 p-4 bg-zinc-50">
                                                                {/* Customer Info */}
                                                                {(order.phone || order.address) && (
                                                                    <div className="mb-4 p-3 bg-white rounded-lg border border-zinc-200 space-y-1">
                                                                        {order.phone && (
                                                                            <p className="flex items-center gap-2 text-sm text-zinc-600">
                                                                                <Phone size={14} className="text-zinc-400" />
                                                                                {order.phone}
                                                                            </p>
                                                                        )}
                                                                        {order.address && (
                                                                            <p className="flex items-center gap-2 text-sm text-zinc-600">
                                                                                <MapPin size={14} className="text-zinc-400" />
                                                                                {order.address}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Items */}
                                                                <div className="space-y-1 mb-4">
                                                                    {order.items?.filter(i => !(i.is_fee || i.source === 'taxa')).map((item, itemIdx) => (
                                                                        <div
                                                                            key={item.id}
                                                                            className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-zinc-100"
                                                                        >
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-500 text-xs font-medium flex items-center justify-center">
                                                                                    {itemIdx + 1}
                                                                                </span>
                                                                                <span className="text-zinc-900">{item.name}</span>
                                                                            </div>
                                                                            <div className="flex items-center gap-4">
                                                                                <span className="text-sm text-zinc-500">
                                                                                    {item.quantity} x {formatPrice(item.unit_price)}
                                                                                </span>
                                                                                <span className="font-medium text-zinc-900">
                                                                                    {formatPrice(item.quantity * item.unit_price)} RON
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                    
                                                                    {/* Show transport fee if exists */}
                                                                    {order.items?.filter(i => i.is_fee || i.source === 'taxa').map((item) => (
                                                                        <div
                                                                            key={item.id}
                                                                            className="flex items-center justify-between py-2 px-3 bg-amber-50 rounded-lg border border-amber-200"
                                                                        >
                                                                            <span className="text-amber-800">{item.name}</span>
                                                                            <span className="font-medium text-amber-600">
                                                                                {formatPrice(item.quantity * item.unit_price)} RON
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>

                                                                {/* Total */}
                                                                <div className="pt-3 border-t border-zinc-200 flex justify-between items-center">
                                                                    <span className="font-medium text-zinc-600">TOTAL</span>
                                                                    <span className={`text-xl font-bold ${isStornata ? 'text-red-600 line-through' : 'text-emerald-600'}`}>
                                                                        {formatPrice(order.total)} RON
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
        );
    };

    const renderArchivedStornoSection = () => {
        const months = (searchQuery ? filteredArchivedByMonth : archivedByMonth)
            .map(([monthKey, monthOrders]) => [monthKey, monthOrders.filter(isStornoGroup)])
            .filter(([, list]) => list.length > 0);
        if (months.length === 0) return null;
        const all = months.flatMap(([, list]) => list);
        const total = all.reduce((sum, o) => sum + (o.total || 0), 0);
        const open = expandedMonths['__storno__'];
        return (
            <div data-testid="archived-storno-section" className="border border-red-200 rounded-xl overflow-hidden mt-6">
                <button
                    data-testid="archived-storno-toggle"
                    onClick={() => toggleMonth('__storno__')}
                    className="w-full flex items-center justify-between p-4 bg-red-50 hover:bg-red-100 transition-colors"
                >
                    <div className="flex items-center gap-3">
                        <RotateCcw size={20} className="text-red-500" />
                        <div className="text-left">
                            <h3 className="font-semibold text-red-700">Facturi Stornate</h3>
                            <p data-testid="archived-storno-count" className="text-xs text-red-500">{all.length} facturi stornate în istoric</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span data-testid="archived-storno-total" className="font-bold text-red-600">-{formatPrice(total)} RON</span>
                        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                </button>
                {open && (
                    <div className="p-3 space-y-4 bg-white">
                        {months.map(([monthKey, list]) => (
                            <div key={`storno-${monthKey}`} data-testid={`archived-storno-month-${monthKey}`} className="space-y-3">
                                <div className="flex items-center justify-between px-1 text-sm">
                                    <span className="flex items-center gap-2 font-medium text-zinc-700">
                                        <Calendar size={14} className="text-zinc-400" />
                                        {monthKey} <span className="text-zinc-400">({list.length})</span>
                                    </span>
                                    <span className="font-semibold text-red-600">-{formatPrice(list.reduce((sum, o) => sum + (o.total || 0), 0))} RON</span>
                                </div>
                                {list.map(renderArchivedOrder)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="pb-24" data-testid="facturi-v2-page">
            <h1 className="text-3xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Facturi
            </h1>
            <p className="text-zinc-500 mb-6">Comenzi finalizate</p>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-white border border-zinc-200 rounded-xl p-4">
                    <p className="text-xs text-zinc-500 uppercase tracking-wide">Total Facturi</p>
                    <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {summary.total_orders}
                    </p>
                </div>
                <div className="bg-white border border-zinc-200 rounded-xl p-4">
                    <p className="text-xs text-zinc-500 uppercase tracking-wide">Valoare Totală</p>
                    <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatPrice(summary.total_value)} <span className="text-base font-normal text-zinc-400">RON</span>
                    </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-xs text-amber-600 uppercase tracking-wide">De Încărcat</p>
                    <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {activeOrders.length}
                    </p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-xs text-amber-600 uppercase tracking-wide">Valoare De Încărcat</p>
                    <p className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {formatPrice(activeOrders.reduce((sum, o) => sum + (o.total || 0), 0))} <span className="text-base font-normal text-amber-400">RON</span>
                    </p>
                </div>
            </div>

            {/* Selection Mode Toggle & Actions */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <Button
                    variant={selectMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                        setSelectMode(!selectMode);
                        if (selectMode) clearSelection();
                    }}
                    className={selectMode ? "bg-violet-600 hover:bg-violet-700" : ""}
                >
                    <CheckSquare size={16} className="mr-2" />
                    {selectMode ? 'Ieși din selecție' : 'Selectează facturi'}
                </Button>
                
                {selectMode && (
                    <>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={selectAllInvoices}
                        >
                            Selectează tot
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={clearSelection}
                        >
                            Deselectează
                        </Button>
                        
                        {selectedCount > 0 && (
                            <span className="text-sm text-violet-600 font-medium">
                                {selectedCount} selectate
                            </span>
                        )}
                    </>
                )}
            </div>

            {/* Batch Export Buttons - Show when items selected */}
            {selectMode && selectedCount > 0 && (
                <div className="flex gap-3 mb-4 p-3 bg-violet-50 border border-violet-200 rounded-xl">
                    <span className="text-sm text-violet-700 font-medium flex items-center">
                        Export {selectedCount} facturi (ZIP):
                    </span>
                    <Button
                        size="sm"
                        onClick={handleExportMultiplePDF}
                        className="bg-red-500 hover:bg-red-600"
                    >
                        <Download size={14} className="mr-1" />
                        PDF
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={handleExportMultipleDBF}
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                    >
                        <FileSpreadsheet size={14} className="mr-1" />
                        Excel
                    </Button>
                </div>
            )}

            {/* Top Customers - Cumulated Values */}
            {topCustomers.length > 0 && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <Trophy size={18} className="text-amber-500" />
                            <h3 className="font-semibold text-amber-800">Top Clienți (Valori Cumulate)</h3>
                        </div>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setShowTopCustomers(!showTopCustomers)}
                        >
                            {showTopCustomers ? 'Ascunde' : `Vezi toți (${topCustomers.length})`}
                        </Button>
                    </div>
                    <div className="space-y-2">
                        {(showTopCustomers ? topCustomers : topCustomers.slice(0, 10)).map((customer, idx) => (
                            <div 
                                key={customer.customer_id}
                                className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                                    idx === 0 ? 'bg-amber-200 text-amber-900' :
                                    idx === 1 ? 'bg-zinc-200 text-zinc-800' :
                                    idx === 2 ? 'bg-orange-200 text-orange-900' :
                                    'bg-white text-zinc-700 border border-zinc-100'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <span className="w-6 h-6 rounded-full bg-black/10 flex items-center justify-center text-xs font-bold">
                                        {idx + 1}
                                    </span>
                                    <div>
                                        <span className="font-bold">{customer.customer_id}</span>
                                        {customer.name && (
                                            <span className="text-sm opacity-75 ml-2">({customer.name})</span>
                                        )}
                                        {customer.box_count > 1 && (
                                            <span className="text-xs bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded ml-2">
                                                {customer.box_count} cutii
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className="font-bold">{formatPrice(customer.total_value)} RON</span>
                                    <span className="text-xs opacity-60 ml-2">({customer.total_qty} buc)</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Loading */}
            {loading ? (
                <div className="space-y-3">
                    <Skeleton className="h-20 w-full rounded-xl" />
                    <Skeleton className="h-20 w-full rounded-xl" />
                    <Skeleton className="h-20 w-full rounded-xl" />
                </div>
            ) : orders.length === 0 ? (
                <div className="bg-zinc-100 border border-dashed border-zinc-300 rounded-xl p-8 text-center">
                    <FileText size={48} className="mx-auto text-zinc-400 mb-3" />
                    <h2 className="font-bold text-zinc-700 mb-2">Nicio factură</h2>
                    <p className="text-zinc-500">Comenzile finalizate vor apărea aici</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {/* Search Bar */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                        <Input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Caută după nume, ID client sau nr. factură..."
                            className="pl-10 pr-10 h-11 bg-white"
                            data-testid="search-invoices"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>
                    
                    {/* Search Results Info */}
                    {searchQuery && (
                        <div className="text-sm text-zinc-500 px-1">
                            {filteredOrders.length + filteredArchivedOrders.length} {(filteredOrders.length + filteredArchivedOrders.length) === 1 ? 'rezultat' : 'rezultate'} pentru "{searchQuery}"
                            {filteredArchivedOrders.length > 0 && (
                                <span className="text-amber-600 ml-1">
                                    ({filteredArchivedOrders.length} în istoric)
                                </span>
                            )}
                        </div>
                    )}
                    
                    {/* Invoices List */}
                    {filteredOrders.length === 0 ? (
                        <div className="bg-zinc-100 border border-dashed border-zinc-300 rounded-xl p-6 text-center">
                            <Search size={32} className="mx-auto text-zinc-400 mb-2" />
                            <p className="text-zinc-500">Nicio factură găsită pentru "{searchQuery}"</p>
                        </div>
                    ) : filteredOrders.map((order, idx) => {
                        const invoiceNumber = getInvoiceNumber(order, idx);
                        const isStornata = order.stornata;
                        const isSelected = selectedInvoices[invoiceNumber] || false;
                        return (
                            <Fragment key={order.customer_id + '-' + order.invoice_number}>
                            <div
                                className={`bg-white border rounded-xl overflow-hidden ${
                                    isStornata 
                                        ? 'border-red-300 bg-red-50' 
                                        : isSelected 
                                            ? 'border-violet-400 bg-violet-50'
                                            : 'border-zinc-200'
                                }`}
                            >
                                {/* Order Header */}
                                <div className={`w-full p-4 flex items-center justify-between transition-colors ${
                                    isStornata 
                                        ? 'hover:bg-red-100' 
                                        : 'hover:bg-zinc-50'
                                }`}>
                                    <div className="flex items-center gap-3">
                                        {/* Checkbox for selection mode */}
                                        {selectMode && !isStornata && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleInvoiceSelection(invoiceNumber);
                                                }}
                                                className={`w-6 h-6 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                                                    isSelected 
                                                        ? 'bg-violet-500 border-violet-500 text-white' 
                                                        : 'border-zinc-300 hover:border-violet-400'
                                                }`}
                                            >
                                                {isSelected && <Check size={14} />}
                                            </button>
                                        )}
                                        
                                        <button
                                            onClick={() => toggleOrder(order)}
                                            className="flex items-center gap-3"
                                        >
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                isStornata 
                                                    ? 'bg-red-200' 
                                                    : 'bg-emerald-100'
                                            }`}>
                                                <span className={`font-bold text-sm ${
                                                    isStornata 
                                                        ? 'text-red-700' 
                                                        : 'text-emerald-700'
                                                }`}>{order.customer_id}</span>
                                            </div>
                                            <div className="text-left">
                                                <div className="flex items-center gap-2">
                                                    <span className={`font-bold ${
                                                        isStornata 
                                                            ? 'text-red-600 line-through' 
                                                            : 'text-violet-600'
                                                    }`}>{invoiceNumber}</span>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); startEditInvoiceNumber(order); }}
                                                        className="p-0.5 text-zinc-400 hover:text-blue-500 hover:bg-blue-50 rounded"
                                                        title="Editează număr factură"
                                                    >
                                                        <Edit2 size={12} />
                                                    </button>
                                                    {isStornata && (
                                                        <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-medium">
                                                            STORNATĂ
                                                        </span>
                                                    )}
                                                    {order.name && <span className="text-zinc-500">- {order.name}</span>}
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-zinc-400">
                                                    <span>{order.item_count} produse</span>
                                                    {order.completed_at && (
                                                        <span className="flex items-center gap-1">
                                                            <Calendar size={10} />
                                                            {formatDate(order.completed_at)}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={(e) => { e.stopPropagation(); handleExportPDF(order, invoiceNumber); }}
                                            size="sm"
                                            className="bg-red-500 hover:bg-red-600 h-8 px-2"
                                            disabled={isStornata || order.has_storno}
                                        >
                                            <Download size={14} className="mr-1" />
                                            PDF
                                        </Button>
                                        <Button
                                            onClick={(e) => { e.stopPropagation(); handleExportDBF(order, invoiceNumber); }}
                                            size="sm"
                                            variant="outline"
                                            className="border-blue-300 text-blue-600 hover:bg-blue-50 h-8 px-2"
                                            disabled={isStornata || order.has_storno}
                                        >
                                            <FileSpreadsheet size={14} className="mr-1" />
                                            Excel
                                        </Button>
                                        <Button
                                            onClick={(e) => { e.stopPropagation(); openGenerateStornoModal(order, invoiceNumber); }}
                                            size="sm"
                                            variant="outline"
                                            className="border-orange-300 text-orange-600 hover:bg-orange-50 h-8 px-2"
                                            title="Generează factură storno"
                                            disabled={isStornata || order.has_storno}
                                        >
                                            <RotateCcw size={14} className="mr-1" />
                                            Storno
                                        </Button>
                                        <Button
                                            onClick={(e) => { e.stopPropagation(); archiveInvoice(order); }}
                                            size="sm"
                                            variant="outline"
                                            className="border-zinc-300 text-zinc-600 hover:bg-zinc-100 h-8 px-2"
                                            title="Mută în Istoric"
                                            disabled={isStornata}
                                        >
                                            <Archive size={14} />
                                        </Button>
                                        {order.has_storno && (
                                            <span className="text-xs text-orange-600 font-medium px-2 py-0.5 bg-orange-100 rounded">
                                                Storno: {order.has_storno}
                                            </span>
                                        )}
                                        <span className={`font-bold text-lg ${
                                            isStornata 
                                                ? 'text-red-600 line-through' 
                                                : 'text-emerald-600'
                                        }`}>
                                            {formatPrice(order.total)} RON
                                        </span>
                                        <button 
                                            onClick={() => toggleOrder(order)}
                                        >
                                            {isOrderExpanded(order) ? (
                                                <ChevronUp size={18} className="text-zinc-400" />
                                            ) : (
                                                <ChevronDown size={18} className="text-zinc-400" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                {isOrderExpanded(order) && (
                                    <div className="border-t border-zinc-100 p-4 bg-zinc-50">
                                        {/* Customer Info */}
                                        {(order.phone || order.address) && (
                                            <div className="mb-4 p-3 bg-white rounded-lg border border-zinc-200 space-y-1">
                                                {order.phone && (
                                                    <p className="flex items-center gap-2 text-sm text-zinc-600">
                                                        <Phone size={14} className="text-zinc-400" />
                                                        {order.phone}
                                                    </p>
                                                )}
                                                {order.address && (
                                                    <p className="flex items-center gap-2 text-sm text-zinc-600">
                                                        <MapPin size={14} className="text-zinc-400" />
                                                        {order.address}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        {/* Courier Toggle */}
                                        <div className="mb-4 p-3 bg-white rounded-lg border border-zinc-200 flex items-center justify-between">
                                            <Label className="flex items-center gap-2 text-sm text-zinc-600">
                                                <Truck size={14} className="text-amber-500" />
                                                Curier organizat
                                            </Label>
                                            <button
                                                onClick={() => handleCourierChange(order.customer_id, !courierData[order.customer_id])}
                                                className={`w-12 h-6 rounded-full transition-colors relative ${
                                                    courierData[order.customer_id] ? 'bg-emerald-500' : 'bg-zinc-300'
                                                }`}
                                            >
                                                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                                    courierData[order.customer_id] ? 'left-7' : 'left-1'
                                                }`} />
                                            </button>
                                        </div>

                                        {/* Items */}
                                        <div className="space-y-1 mb-4">
                                            {order.items?.filter(i => !(i.is_fee || i.source === 'taxa')).map((item, itemIdx) => (
                                                <div
                                                    key={item.id}
                                                    className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-zinc-100"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-6 h-6 rounded-full bg-zinc-100 text-zinc-500 text-xs font-medium flex items-center justify-center">
                                                            {itemIdx + 1}
                                                        </span>
                                                        <span className="text-zinc-900">{item.name}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-sm text-zinc-500">
                                                            {item.quantity} x {formatPrice(item.unit_price)}
                                                        </span>
                                                        <span className="font-medium text-zinc-900">
                                                            {formatPrice(item.quantity * item.unit_price)} RON
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                            
                                            {/* Show transport fee if exists */}
                                            {order.items?.filter(i => i.is_fee || i.source === 'taxa').map((item) => (
                                                <div
                                                    key={item.id}
                                                    className="flex items-center justify-between py-2 px-3 bg-amber-50 rounded-lg border border-amber-200"
                                                >
                                                    <span className="text-amber-800">{item.name}</span>
                                                    <span className="font-medium text-amber-600">
                                                        {formatPrice(item.quantity * item.unit_price)} RON
                                                    </span>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Total */}
                                        <div className="pt-3 border-t border-zinc-200 flex justify-between items-center mb-4">
                                            <span className="font-medium text-zinc-600">TOTAL</span>
                                            <span className={`text-xl font-bold ${isStornata ? 'text-red-600 line-through' : 'text-emerald-600'}`}>
                                                {formatPrice(order.total)} RON
                                            </span>
                                        </div>
                                        
                                        {/* Edit Button */}
                                        <div className="flex gap-3">
                                            <Button
                                                onClick={() => handleEditClick(order)}
                                                variant="outline"
                                                className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                                            >
                                                <Edit2 size={16} className="mr-2" />
                                                Editează Date Factură
                                            </Button>
                                        </div>
                                        
                                        {/* Storno Button */}
                                        {!isStornata && (
                                            <div className="mt-3 pt-3 border-t border-zinc-200">
                                                <Button
                                                    onClick={() => setStornoConfirm({ order, invoice_number: invoiceNumber })}
                                                    variant="outline"
                                                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                                                >
                                                    <RotateCcw size={16} className="mr-2" />
                                                    Storno Factură
                                                </Button>
                                            </div>
                                        )}
                                        
                                        {/* Storno info if already stornata + Cancel Storno button */}
                                        {isStornata && (
                                            <div className="mt-3 pt-3 border-t border-red-200">
                                                <div className="text-center text-sm text-red-600 mb-3">
                                                    <AlertTriangle size={14} className="inline mr-1" />
                                                    Stornată la: {formatDate(order.stornata_at)}
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button
                                                        onClick={() => setCancelStornoConfirm({ order, invoice_number: invoiceNumber })}
                                                        variant="outline"
                                                        className="flex-1 border-green-300 text-green-600 hover:bg-green-50"
                                                    >
                                                        <RotateCcw size={16} className="mr-2" />
                                                        Anulează Storno
                                                    </Button>
                                                    <Button
                                                        onClick={(e) => { e.stopPropagation(); archiveInvoice(order); }}
                                                        variant="outline"
                                                        className="border-zinc-300 text-zinc-600 hover:bg-zinc-50"
                                                        title="Mută în Istoric Facturi"
                                                    >
                                                        <Archive size={16} />
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            </Fragment>
                        );
                    })}
                </div>
            )}

            {/* History Section - Archived Invoices */}
            {archivedOrders.length > 0 && (
                <div className="mt-8">
                    <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="w-full flex items-center justify-between p-4 bg-zinc-100 hover:bg-zinc-200 rounded-xl transition-colors"
                    >
                        <div className="flex items-center gap-3">
                            <Archive size={24} className="text-zinc-500" />
                            <div className="text-left">
                                <h2 className="font-semibold text-zinc-800">Istoric Facturi</h2>
                                <p className="text-sm text-zinc-500">{archivedOrders.length} facturi exportate</p>
                            </div>
                        </div>
                        {showHistory ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    
                    {showHistory && (
                        <div className="mt-4 space-y-4">
                            {(searchQuery ? filteredArchivedByMonth : archivedByMonth).length === 0 ? (
                                <div className="text-center text-zinc-500 py-4">
                                    {searchQuery ? `Nicio factură găsită în istoric pentru "${searchQuery}"` : 'Nicio factură în istoric'}
                                </div>
                            ) : (searchQuery ? filteredArchivedByMonth : archivedByMonth).map(([monthKey, monthOrders]) => {
                                // Calculate net total for the month (subtract stornări)
                                const monthNetTotal = monthOrders.reduce((sum, order) => {
                                    if (order.stornata) {
                                        // Stornată - subtract from total
                                        return sum - (order.total || 0);
                                    }
                                    return sum + (order.total || 0);
                                }, 0);
                                
                                return (
                                <div key={monthKey} className="border border-zinc-200 rounded-xl overflow-hidden">
                                    <button
                                        onClick={() => toggleMonth(monthKey)}
                                        className="w-full flex items-center justify-between p-3 bg-zinc-50 hover:bg-zinc-100"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Calendar size={18} className="text-zinc-400" />
                                            <span className="font-medium text-zinc-700">{monthKey}</span>
                                            <span className="text-sm text-zinc-400">({monthOrders.filter(o => !isStornoGroup(o)).length} facturi)</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-semibold text-emerald-600 text-sm">
                                                {formatPrice(monthNetTotal)} RON
                                            </span>
                                            {expandedMonths[monthKey] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                        </div>
                                    </button>
                                    
                                    {expandedMonths[monthKey] && (
                                        <div className="space-y-3 p-3 bg-white">
                                            {monthOrders.filter(o => !isStornoGroup(o)).map(renderArchivedOrder)}
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                            {renderArchivedStornoSection()}
                        </div>
                    )}
                </div>
            )}

            {/* Storno Confirmation Dialog */}
            <Dialog open={!!stornoConfirm} onOpenChange={() => setStornoConfirm(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle size={20} />
                            Confirmare Storno
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-zinc-600">
                            Ești sigur că vrei să stornezi factura <strong>{stornoConfirm?.invoice_number}</strong> pentru clientul <strong>{stornoConfirm?.order?.customer_id}</strong>?
                        </p>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                            <strong>Atenție:</strong> Produsele din factură vor fi returnate automat în magazin.
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => setStornoConfirm(null)}
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={() => handleStorno(stornoConfirm?.order)}
                                className="flex-1 bg-red-600 hover:bg-red-700"
                            >
                                <RotateCcw size={16} className="mr-2" />
                                Confirmă Storno
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Invoice Dialog */}
            <Dialog open={!!editingInvoice} onOpenChange={() => setEditingInvoice(null)}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-blue-600">
                            <Edit2 size={20} />
                            Editare Factură {editingInvoice?.invoice_number}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Customer Selector */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Selectează Client Existent</Label>
                            <div className="relative">
                                <Input
                                    value={customerSearch}
                                    onChange={(e) => {
                                        setCustomerSearch(e.target.value);
                                        setShowCustomerDropdown(true);
                                    }}
                                    onFocus={() => setShowCustomerDropdown(true)}
                                    placeholder="Caută client după cod, nume sau telefon..."
                                    className="w-full"
                                />
                                {showCustomerDropdown && filteredCustomers.length > 0 && (
                                    <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                        {filteredCustomers.map(c => (
                                            <button
                                                key={c.customer_id}
                                                onClick={() => selectCustomer(c)}
                                                className="w-full px-3 py-2 text-left hover:bg-violet-50 border-b border-zinc-100 last:border-b-0"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="font-medium text-violet-600">{c.customer_id}</span>
                                                    <span className="text-xs text-zinc-400">{c.phone}</span>
                                                </div>
                                                <div className="text-sm text-zinc-600 truncate">{c.name || 'Fără nume'}</div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Customer Info */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Nume Client</Label>
                                <Input
                                    value={editForm.name}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Nume client..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Telefon</Label>
                                <Input
                                    value={editForm.phone}
                                    onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="Telefon..."
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Adresă</Label>
                            <Input
                                value={editForm.address}
                                onChange={(e) => setEditForm(prev => ({ ...prev, address: e.target.value }))}
                                placeholder="Adresa..."
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Data Facturii</Label>
                            <Input
                                type="date"
                                value={editForm.date}
                                onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                            />
                        </div>

                        {/* Products */}
                        <div className="border-t pt-4 mt-4">
                            <Label className="text-base font-semibold mb-3 block">Produse</Label>
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {editForm.items.map((item, idx) => (
                                    <div key={item.id} className="flex items-center gap-2 p-2 bg-zinc-50 rounded-lg">
                                        <span className="text-xs text-zinc-500 w-6">{idx + 1}.</span>
                                        <span className="flex-1 text-sm truncate" title={item.name}>
                                            {item.is_fee ? 'Taxa transport' : item.name}
                                        </span>
                                        <div className="flex items-center gap-1">
                                            <Input
                                                type="number"
                                                min="1"
                                                value={item.quantity}
                                                onChange={(e) => updateItemField(item.id, 'quantity', e.target.value)}
                                                className="w-16 h-8 text-center text-sm"
                                            />
                                            <span className="text-xs text-zinc-400">x</span>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                value={item.unit_price}
                                                onChange={(e) => updateItemField(item.id, 'unit_price', e.target.value)}
                                                className="w-20 h-8 text-right text-sm"
                                            />
                                            <span className="text-xs text-zinc-500 w-16 text-right">
                                                = {((parseFloat(item.unit_price) || 0) * (parseInt(item.quantity) || 0)).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 pt-3 border-t flex justify-between items-center">
                                <span className="font-semibold">TOTAL:</span>
                                <span className="text-lg font-bold text-emerald-600">
                                    {calculateEditTotal().toFixed(2)} RON
                                </span>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => setEditingInvoice(null)}
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={handleEditSave}
                                className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                                Salvează Modificările
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Invoice Number Dialog */}
            <Dialog open={!!editingInvoiceNumber} onOpenChange={(open) => !open && setEditingInvoiceNumber(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit2 size={18} />
                            Editează Număr Factură
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label>Număr Factură</Label>
                            <Input
                                value={newInvoiceNumber}
                                onChange={(e) => setNewInvoiceNumber(e.target.value)}
                                placeholder="Ex: TOTH0150"
                                className="mt-1"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        saveInvoiceNumber();
                                    }
                                }}
                            />
                        </div>
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                onClick={() => setEditingInvoiceNumber(null)}
                                className="flex-1"
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={saveInvoiceNumber}
                                className="flex-1 bg-blue-600 hover:bg-blue-700"
                            >
                                Salvează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Cancel Storno Confirmation Dialog */}
            <Dialog open={!!cancelStornoConfirm} onOpenChange={() => setCancelStornoConfirm(null)}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-green-600">
                            <RotateCcw size={20} />
                            Anulare Storno
                        </DialogTitle>
                    </DialogHeader>
                    <div className="mt-4">
                        <p className="text-zinc-600">
                            Ești sigur că vrei să anulezi stornarea facturii <strong>{cancelStornoConfirm?.invoice_number}</strong>?
                        </p>
                        <p className="text-sm text-amber-600 mt-2">
                            <AlertTriangle size={14} className="inline mr-1" />
                            Produsele vor fi scoase din magazin și factura va deveni din nou validă.
                        </p>
                    </div>
                    <div className="flex gap-3 mt-4">
                        <Button
                            variant="outline"
                            onClick={() => setCancelStornoConfirm(null)}
                            className="flex-1"
                        >
                            Renunță
                        </Button>
                        <Button
                            onClick={() => handleCancelStorno(cancelStornoConfirm?.order)}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                            Confirmă Anulare
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Storno History Dialog */}
            <Dialog open={showStornoHistory} onOpenChange={setShowStornoHistory}>
                <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History size={20} className="text-zinc-500" />
                            Istoric Stornări
                        </DialogTitle>
                    </DialogHeader>
                    <div className="mt-4 space-y-3">
                        {stornoHistory.length === 0 ? (
                            <p className="text-center text-zinc-500 py-8">Nu există istoric de stornări</p>
                        ) : (
                            stornoHistory.map((entry, idx) => (
                                <div 
                                    key={entry.id || idx} 
                                    className={`p-3 rounded-lg border ${
                                        entry.action === 'storno' 
                                            ? 'bg-red-50 border-red-200' 
                                            : 'bg-green-50 border-green-200'
                                    }`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                                entry.action === 'storno'
                                                    ? 'bg-red-100 text-red-700'
                                                    : 'bg-green-100 text-green-700'
                                            }`}>
                                                {entry.action === 'storno' ? 'STORNO' : 'ANULARE STORNO'}
                                            </span>
                                            <p className="mt-1 font-medium">Factură: {entry.invoice_number}</p>
                                            <p className="text-sm text-zinc-600">Client: {entry.customer_id}</p>
                                        </div>
                                        <span className="text-xs text-zinc-400">
                                            {formatDate(entry.created_at)}
                                        </span>
                                    </div>
                                    {entry.returned_products && entry.returned_products.length > 0 && (
                                        <div className="mt-2 text-sm text-zinc-600">
                                            <span className="font-medium">Produse returnate în magazin:</span>
                                            <ul className="ml-4 mt-1">
                                                {entry.returned_products.slice(0, 3).map((p, i) => (
                                                    <li key={i}>• {p.name} x{p.quantity}</li>
                                                ))}
                                                {entry.returned_products.length > 3 && (
                                                    <li className="text-zinc-400">... și încă {entry.returned_products.length - 3}</li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                    {entry.removed_from_store && entry.removed_from_store.length > 0 && (
                                        <div className="mt-2 text-sm text-zinc-600">
                                            <span className="font-medium">Produse scoase din magazin:</span>
                                            <ul className="ml-4 mt-1">
                                                {entry.removed_from_store.slice(0, 3).map((p, i) => (
                                                    <li key={i}>• {p.name} x{p.quantity}</li>
                                                ))}
                                                {entry.removed_from_store.length > 3 && (
                                                    <li className="text-zinc-400">... și încă {entry.removed_from_store.length - 3}</li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    <div className="mt-4 pt-3 border-t">
                        <Button
                            variant="outline"
                            onClick={() => loadStornoHistory()}
                            className="w-full"
                        >
                            <History size={16} className="mr-2" />
                            Vezi Tot Istoricul
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Generate Storno Modal */}
            <Dialog open={!!generateStornoModal} onOpenChange={(open) => { if (!open) setGenerateStornoModal(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-orange-600">
                            <RotateCcw size={20} />
                            Generează Factură Storno
                        </DialogTitle>
                    </DialogHeader>
                    
                    {generateStornoModal && (
                        <div className="space-y-4">
                            <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                                <p className="text-sm text-zinc-700">
                                    Vei genera o <strong>factură storno nouă</strong> pentru:
                                </p>
                                <p className="mt-2 font-bold text-lg text-orange-700">
                                    {generateStornoModal.invoiceNumber}
                                </p>
                                {generateStornoModal.order.name && (
                                    <p className="text-sm text-zinc-600">
                                        Client: {generateStornoModal.order.name}
                                    </p>
                                )}
                                <p className="text-sm text-zinc-600">
                                    Valoare: {formatPrice(generateStornoModal.order.total)} RON
                                </p>
                            </div>
                            
                            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-lg space-y-2">
                                <p className="font-medium text-zinc-700">Ce se va întâmpla:</p>
                                <ul className="text-sm text-zinc-600 space-y-1 list-disc list-inside">
                                    <li>Se generează o factură nouă cu cantități negative</li>
                                    <li>Produsele se întorc în magazin</li>
                                    <li>Factura storno primește următorul număr disponibil</li>
                                </ul>
                            </div>
                            
                            {/* Transport fee option */}
                            <div className="flex items-center justify-between p-3 bg-white border border-zinc-200 rounded-lg">
                                <Label className="flex items-center gap-2 text-sm text-zinc-700">
                                    <Truck size={16} className="text-amber-500" />
                                    Include taxa de transport în storno?
                                </Label>
                                <button
                                    onClick={() => setStornoIncludeTransport(!stornoIncludeTransport)}
                                    className={`w-12 h-6 rounded-full transition-colors relative ${
                                        stornoIncludeTransport ? 'bg-emerald-500' : 'bg-zinc-300'
                                    }`}
                                >
                                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                        stornoIncludeTransport ? 'left-7' : 'left-1'
                                    }`} />
                                </button>
                            </div>
                            
                            <div className="flex gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    onClick={() => setGenerateStornoModal(null)}
                                    className="flex-1"
                                    disabled={generatingStorno}
                                >
                                    Anulează
                                </Button>
                                <Button
                                    onClick={handleGenerateStorno}
                                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                                    disabled={generatingStorno}
                                >
                                    {generatingStorno ? (
                                        <>Generare...</>
                                    ) : (
                                        <>
                                            <RotateCcw size={16} className="mr-2" />
                                            Generează Storno
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default FacturiV2;
